import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantDropdown } from './useTenantDropdown';
import { useSandboxDropdown } from 'SandboxModule/hooks/useSandboxDropdown';
import { Tenant } from 'TenantModule/types';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { useTenants } from './useTenants';
import { getIsTenantEnabled } from 'utils';
import { Realm } from 'types';
import { useSwitchTenantMutation } from 'TenantModule/api/queries/useSwitchTenant';
import { useNotification } from 'hooks/useNotification';

export const useTenantRealmDropdownsFlow = ({
  realmId,
}: {
  realmId?: Realm['realm_id'];
} = {}) => {
  const { openTenantDropdown, selectedGlobalTenant, selectedTenant, setSelectedLocalTenant } =
    useTenantDropdown();
  const {
    openRealmDropdown,
    selectedLocalSandbox,
    selectedGlobalRealm,
    handleSelectGlobalRailsSandbox,
    isFetchingSandboxes,
  } = useSandboxDropdown({
    isoStateId: SandboxIsoStateIds.AppbarRealmSelector,
    realmId,
  });

  const { getTenant, handleSelectGlobalTenant } = useTenants();
  const { getSandbox, getRealmIdsByTenant } = useSandboxes();
  const { showError } = useNotification();
  const navigate = useNavigate();

  const { switchTenant } = useSwitchTenantMutation();

  /**
   * Switches tenant/realm context by navigating with URL params.
   * Auth defaults are immutable - only URL params change.
   */
  const onSetGlobalTenantRealm = useCallback(
    ({ tenantId, realmId }: { tenantId: Tenant['id']; realmId: Realm['realm_id'] }) => {
      const tenant = getTenant({ tenantId });
      const realm = getSandbox(realmId);

      if (tenant && realm) {
        // Call switch tenant API to update server-side session, then navigate
        switchTenant(
          {
            tenant: tenant.tenantName,
          },
          {
            onSuccess: () => {
              // Navigate with context in URL (no page reload, no localStorage update)
              const currentPath = window.location.pathname;
              navigate(
                `${currentPath}?tenant=${encodeURIComponent(tenant.tenantName)}&realmId=${encodeURIComponent(realm.realm_id)}`,
              );
            },
            onError: () => {
              showError('Failed to switch tenant');
            },
          },
        );
      } else {
        showError("Tenant or Realm doesn't exist");
      }
    },
    [getSandbox, getTenant, navigate, showError, switchTenant],
  );

  const onSelectRealm = ({
    target,
    tenantId,
    isSelectingGlobalRealm = false,
    onSelect,
  }: {
    target: HTMLElement;
    /**
     * If undefined, then the dropdown will show All the realms
     * If provided, will show only the realms under this tenant
     */
    tenantId?: number;
    isSelectingGlobalRealm?: boolean;
    onSelect?: (args: { realm: Realm }) => void;
  }) => {
    const tenant = tenantId && getIsTenantEnabled() ? getTenant({ tenantId }) : undefined;
    void tenant; // Used for side effects

    // tenant.realms (from /tenant/getAll) can be incomplete and miss sandbox
    // realms that actually belong to the tenant. Union it with realms derived
    // from the sandbox cache (filtered by tenant) so the dropdown reflects
    // every realm a user can act on.
    const realms = tenant
      ? Array.from(new Set([...tenant.realms, ...getRealmIdsByTenant(tenant.tenantName)]))
      : [];

    openRealmDropdown({
      target,
      isSelectingGlobalRealm,
      onSelect,
      primaryRealmId: tenant?.primaryRealm ?? null,
      realms,
    });
  };

  /**
   * GLX-1922: pick a tenant without being marched through the realm dropdown.
   *
   * onSelectTenantRealm below defers setSelectedLocalTenant until a realm is
   * chosen, which is wrong when the realm is about to be created rather than
   * selected: it forces the operator to pick an irrelevant existing realm, and
   * makes a tenant with no existing realms unselectable at all.
   */
  const onSelectTenantOnly = ({ target }: { target: HTMLElement }) => {
    openTenantDropdown({ target });
  };

  const onSelectTenantRealm = ({
    tenantDropdownTarget,
    realmDropdownTarget,
    onDone,
  }: {
    tenantDropdownTarget: HTMLElement;
    realmDropdownTarget: HTMLElement;
    onDone?: (args: { tenant: Tenant; realm: Realm }) => void;
  }) => {
    openTenantDropdown({
      target: tenantDropdownTarget,
      shouldSkipSetTenant: true,
      onSelect: ({ tenant }) => {
        onSelectRealm({
          target: realmDropdownTarget,
          tenantId: tenant.id,
          onSelect: ({ realm }) => {
            onDone?.({ realm: realm, tenant });

            setSelectedLocalTenant(tenant);
          },
        });
      },
    });
  };

  return {
    selectedRealm: selectedLocalSandbox,
    selectedTenant,
    selectedGlobalRealm,
    selectedGlobalTenant,
    onSelectTenantRealm,
    onSelectTenantOnly,
    onSelectRealm,
    onSetGlobalTenantRealm,
    isFetchingSandboxes,
  };
};
